import type { NodeSpecItem, EnvironmentalParameterItem, MaterialsData } from "../../../ThermalDesignTypes";
import { ThermalConfig } from "../types/ThermalConfig";
import type { ThermalModelConfig, PanelData, PanelCoating, CoatingPreset, CellData } from '../types/ThermalConfig';
import type { SubsystemThresholdsMap, Thresholds } from "../types/SurfaceThresholdsTypes";
import { THRESHOLD_LEVELS, createEmptyThresholds } from "../types/SurfaceThresholdsTypes";

const normalizeConductanceRow = (row: number[] | unknown, count: number): number[] => {
  const base = Array.isArray(row) ? (row as number[]) : [];
  if (base.length > count) return base.slice(0, count);
  if (base.length < count) return [...base, ...Array(count - base.length).fill(0)];
  return base;
};

export const convertFrontendToBackend = (
  nodeSpecsOptions: NodeSpecItem[],
  environmentalParameters: EnvironmentalParameterItem[],
  existingThermalModel?: ThermalModelConfig
): ThermalConfig => {
  const count = nodeSpecsOptions.length;

  const thermalConfig: ThermalConfig = {
    count,
    nodes_per_face: nodeSpecsOptions.find((n) => !n.values?.node_isInternal)?.nodes_per_face ?? 1,
    node_surface_area: [],
    node_mass: [],
    node_specific_heat: [],
    node_absorptivity: [],
    node_emissivity: [],
    node_body_vec: [],
    node_conductance: [],
    node_initial_temperature: [],
    node_internal_power: [],
    nodeMaterial: [],
    albedo_factor: 0,
    blackbody_temperature: 0,
    solar_constant: 0,
  };

  nodeSpecsOptions.forEach((node) => {
    const values = node.values;
    const isUnavailable = node.status === "unavailable";

    thermalConfig.node_surface_area.push(isUnavailable ? null : (values?.node_surface_area ?? 0));
    thermalConfig.node_mass.push(isUnavailable ? null : (values?.node_mass ?? 0));
    thermalConfig.node_specific_heat.push(isUnavailable ? null : (values?.node_specific_heat ?? 0));
    thermalConfig.node_absorptivity.push(isUnavailable ? null : (values?.node_absorptivity ?? 0));
    thermalConfig.node_emissivity.push(isUnavailable ? null : (values?.node_emissivity ?? 0));
    thermalConfig.node_body_vec.push(isUnavailable ? null : (values?.node_body_vec ?? [0, 0, 0]));
    thermalConfig.node_conductance.push(isUnavailable ? null : normalizeConductanceRow(values?.node_conductance, count));
    thermalConfig.node_initial_temperature.push(isUnavailable ? null : (values?.node_initial_temperature ?? 0));
    thermalConfig.node_internal_power.push(isUnavailable ? null : (values?.node_internal_power ?? 0));
    thermalConfig.nodeMaterial!.push(isUnavailable ? "" : (values?.node_material ?? ""));
  });

  const albedoParam = environmentalParameters.find((p) => p.key === "albedo_factor");
  const blackbodyParam = environmentalParameters.find((p) => p.key === "blackbody_temperature");
  const solarParam = environmentalParameters.find((p) => p.key === "solar_constant");

  thermalConfig.albedo_factor = albedoParam?.value ?? 0.306;
  thermalConfig.blackbody_temperature = blackbodyParam?.value ?? 255;
  thermalConfig.solar_constant = solarParam?.value ?? 1361;

  thermalConfig.thermalModel = extractThermalModelFromFrontend(nodeSpecsOptions, existingThermalModel);

  return thermalConfig;
};

export const convertBackendToFrontend = (
  backendConfig: ThermalConfig,
  existingLocalNodes: NodeSpecItem[],
  materialsData: MaterialsData[] = []
): NodeSpecItem[] => {
  const mergedNodes: NodeSpecItem[] = [];

  for (let i = 0; i < backendConfig.count; i++) {
    const existingNode = existingLocalNodes[i] ?? existingLocalNodes.find((n) => n.id === String(i + 1));
    const nodeId = existingNode?.id ?? String(i + 1);

    let status: "complete" | "unconfigured" | "cancelled" | "unavailable";
    const isUnavailable = backendConfig.node_surface_area[i] === null;

    if (isUnavailable) {
      status = "unavailable";
    } else if (backendConfig.configComplete === true) {
      status = "complete";
    } else {
      status = determineNodeStatus(backendConfig, i);
    }

    const mergedNode: NodeSpecItem = {
      name: existingNode?.name ?? `node_${String(i + 1).padStart(2, "0")}`,
      id: nodeId,
      status,
      isSelected: existingNode?.isSelected ?? false,
      nodes_per_face: ((backendConfig.nodes_per_face ?? existingNode?.nodes_per_face ?? 1) as 1 | 5 | 9),
      values: {
        node_surface_area: backendConfig.node_surface_area[i] ?? 0,
        node_mass: backendConfig.node_mass[i] ?? 0,
        node_specific_heat: backendConfig.node_specific_heat[i] || (existingNode?.values?.node_isInternal ? 896 : 0),
        node_absorptivity: backendConfig.node_absorptivity[i] ?? 0,
        node_emissivity: backendConfig.node_emissivity[i] ?? 0,
        node_body_vec: backendConfig.node_body_vec[i] ?? [0, 0, 0],
        node_conductance: normalizeConductanceRow(backendConfig.node_conductance[i] ?? Array(backendConfig.count).fill(0), backendConfig.count),
        node_initial_temperature: backendConfig.node_initial_temperature[i] ?? 0,
        node_internal_power: backendConfig.node_internal_power[i] ?? 0,
        node_material: backendConfig.nodeMaterial?.[i] || existingNode?.values?.node_material || "",
        node_isInternal: existingNode?.values?.node_isInternal ?? false,
        node_internal_distances: existingNode?.values?.node_internal_distances,
        node_material_properties: (() => {
          if (existingNode?.values?.node_material_properties) return existingNode.values.node_material_properties;
          const materialName = backendConfig.nodeMaterial?.[i] || existingNode?.values?.node_material || "";
          if (!materialName) return undefined;
          const found = materialsData.find((m) => m.material_name === materialName);
          if (found) return found;
          return {
            material_name: materialName,
            specific_heat: backendConfig.node_specific_heat[i] ?? undefined,
            absorptivity: backendConfig.node_absorptivity[i] ?? undefined,
            emissivity: backendConfig.node_emissivity[i] ?? undefined,
            density: null,
          } as MaterialsData;
        })(),
      },
    };

    mergedNodes.push(mergedNode);
  }

  const mergedNodeIds = new Set(mergedNodes.map((n) => n.id));
  const localOnlyNodes = existingLocalNodes.filter((node) => !mergedNodeIds.has(node.id));

  const allNodes = [...mergedNodes, ...localOnlyNodes];

  if (backendConfig.thermalModel) {
    return mergeThermalModelToFrontend(backendConfig.thermalModel, allNodes);
  }

  return allNodes;
};

export const extractEnvironmentalParameters = (
  backendConfig: ThermalConfig,
  currentParams: EnvironmentalParameterItem[]
): EnvironmentalParameterItem[] => {
  return currentParams.map((param) => {
    switch (param.key) {
      case "albedo_factor":
        return { ...param, value: backendConfig.albedo_factor ?? param.value };
      case "blackbody_temperature":
        return { ...param, value: backendConfig.blackbody_temperature ?? param.value };
      case "solar_constant":
        return { ...param, value: backendConfig.solar_constant ?? param.value };
      default:
        return param;
    }
  });
};

const determineNodeStatus = (
  config: ThermalConfig,
  nodeIndex: number
): "complete" | "unconfigured" | "cancelled" | "unavailable" => {
  const surfaceArea = config.node_surface_area[nodeIndex];
  const mass = config.node_mass[nodeIndex];
  const temp = config.node_initial_temperature[nodeIndex];

  const hasValues =
    surfaceArea !== null && surfaceArea !== undefined && surfaceArea > 0 &&
    mass !== null && mass !== undefined && mass > 0 &&
    temp !== null && temp !== undefined;

  return hasValues ? "complete" : "unconfigured";
};

export const mergeEnvironmentalParameters = (
  backendParams: Partial<{ albedo_factor: number; blackbody_temperature: number; solar_constant: number }>,
  localParams: EnvironmentalParameterItem[]
): EnvironmentalParameterItem[] => {
  return localParams.map((param) => {
    const backendValue = backendParams[param.key as keyof typeof backendParams];
    return backendValue !== undefined ? { ...param, value: backendValue } : param;
  });
};

const getPanelAxisLabel = (panelIndex: number, isInternal: boolean, nodeId: string): string => {
  if (isInternal) return nodeId;
  const axisLabels = ["+X", "-X", "+Y", "-Y", "+Z", "-Z"];
  return axisLabels[panelIndex] ?? nodeId;
};

const getGridDimensions = (
  nodesPerFace: number
): { nodesPerPanel: number; rows: number; cols: number } => {
  switch (nodesPerFace) {
    case 1:
      return { nodesPerPanel: 1, rows: 1, cols: 1 };
    case 9:
      return { nodesPerPanel: 9, rows: 3, cols: 3 };
    default:
      return { nodesPerPanel: 9, rows: 3, cols: 3 };
  }
};

export const extractThermalModelFromFrontend = (
  nodeSpecsOptions: NodeSpecItem[],
  existingThermalModel?: ThermalModelConfig
): ThermalModelConfig => {
  const panels: PanelData[] = [];
  const nodesPerFace = nodeSpecsOptions.find((n) => !n.values?.node_isInternal)?.nodes_per_face ?? 1;
  const { nodesPerPanel: DEFAULT_NODES_PER_PANEL, rows: DEFAULT_ROWS, cols: DEFAULT_COLS } = getGridDimensions(nodesPerFace);

  nodeSpecsOptions.forEach((node, panelIndex) => {
    const values = node.values;
    const frontendCoating = values?.coating;

    const convertCoatingTypeConfig = (
      typeConfig: any
    ): CoatingPreset => {
      return {
        presetId: typeConfig?.preset_id ?? "",
        absorptivity: typeConfig?.absorptivity ?? 0,
        emissivity: typeConfig?.emissivity ?? 0,
        areaDensity: typeConfig?.area_density ?? 0,
        specificHeat: typeConfig?.specific_heat ?? 0,
        thermalConductance: typeConfig?.thermal_conductance ?? 0,
      };
    };

    const cells: CellData[] = [];
    const frontendCells = frontendCoating?.cells ?? Array(DEFAULT_NODES_PER_PANEL).fill("none");
    const hasAnyCoatingPainted = frontendCells.some((c: string) => c !== "none");

    const panelCoating: PanelCoating = {
      enabled: hasAnyCoatingPainted,
      mli: convertCoatingTypeConfig(frontendCoating?.mli),
      osr: convertCoatingTypeConfig(frontendCoating?.osr),
    };
    const axisLabel = getPanelAxisLabel(panelIndex, Boolean(values?.node_isInternal), node.id);

    for (let i = 0; i < DEFAULT_NODES_PER_PANEL; i++) {
      const row = Math.floor(i / DEFAULT_COLS);
      const col = i % DEFAULT_COLS;
      const cellCoating = frontendCells[i] ?? "none";
      const existingCell = existingThermalModel?.panels?.[panelIndex]?.cells?.[i];

      const cell: CellData = {
        localIndex: i,
        row,
        col,
        displayName: `${axisLabel}${i + 1}`,
        coating: cellCoating,
        subsystems: existingCell?.subsystems ?? [],
      };
      cells.push(cell);
    }

    const panel: PanelData = {
      panelIndex,
      panelKind: values?.node_isInternal ? "deck" : "face",
      panelLabel: getPanelAxisLabel(panelIndex, Boolean(values?.node_isInternal), node.id),
      displayName: node.name,
      coating: panelCoating,
      cells,
    };

    panels.push(panel);
  });

  return {
    nodesPerPanel: DEFAULT_NODES_PER_PANEL,
    rows: DEFAULT_ROWS,
    cols: DEFAULT_COLS,
    decks: existingThermalModel?.decks,
    panels,
  };
};

export const mergeThermalModelToFrontend = (
  thermalModel: ThermalModelConfig | undefined,
  existingLocalNodes: NodeSpecItem[]
): NodeSpecItem[] => {
  if (!thermalModel?.panels) {
    return existingLocalNodes;
  }

  const updatedNodes: NodeSpecItem[] = [];
  const backendPanels = thermalModel.panels;

  for (let i = 0; i < backendPanels.length; i++) {
    const backendPanel = backendPanels[i];
    const existingNode = existingLocalNodes[i] ?? existingLocalNodes.find((n) => n.id === backendPanel.panelLabel);

    if (!existingNode || !existingNode.values) {
      updatedNodes.push(existingNode || { id: String(i), name: `panel_${i}`, status: "unconfigured", isSelected: false });
      continue;
    }

    const convertCoatingPresetToFrontend = (
      preset: CoatingPreset
    ): any => {
      return {
        preset_id: preset?.presetId ? preset.presetId : null,
        absorptivity: preset?.absorptivity ?? 0,
        emissivity: preset?.emissivity ?? 0,
        area_density: preset?.areaDensity ?? 0,
        specific_heat: preset?.specificHeat ?? 0,
        thermal_conductance: preset?.thermalConductance ?? 0,
      };
    };

    const cellsArray = backendPanel.cells.map((cell) => cell.coating);
    const counts = { none: 0, mli: 0, osr: 0 };
    cellsArray.forEach((cellCoating) => {
      if (cellCoating in counts) counts[cellCoating as keyof typeof counts]++;
    });

    const mliConfig = convertCoatingPresetToFrontend(backendPanel.coating.mli);
    const osrConfig = convertCoatingPresetToFrontend(backendPanel.coating.osr);
    const noneConfig = { preset_id: null, absorptivity: 0, emissivity: 0, area_density: 0, specific_heat: 0, thermal_conductance: 0 };

    const updatedCoating = {
      mli: { ...mliConfig, percentage: backendPanel.coating.enabled ? (counts.mli / cellsArray.length) * 100 : 0 },
      osr: { ...osrConfig, percentage: backendPanel.coating.enabled ? (counts.osr / cellsArray.length) * 100 : 0 },
      none: { ...noneConfig, percentage: backendPanel.coating.enabled ? (counts.none / cellsArray.length) * 100 : 0 },
      cells: cellsArray,
    };

    const updatedNode: NodeSpecItem = {
      ...existingNode,
      values: {
        ...existingNode.values,
        coating: backendPanel.coating.enabled ? updatedCoating : existingNode.values.coating ?? updatedCoating,
      },
    };

    updatedNodes.push(updatedNode);
  }

  const backendPanelIds = new Set(backendPanels.map((p) => p.panelLabel));
  const localOnlyNodes = existingLocalNodes.filter((node) => !backendPanelIds.has(node.id) && updatedNodes.every((n) => n.id !== node.id));

  return [...updatedNodes, ...localOnlyNodes];
};

export const validateThermalConfig = (config: ThermalConfig): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  for (let i = 0; i < config.count; i++) {
    const surfaceArea = config.node_surface_area[i];
    const mass = config.node_mass[i];

    if (surfaceArea === null || mass === null) continue;

    const conductanceRow = config.node_conductance[i];
    if (Array.isArray(conductanceRow)) {
      conductanceRow.forEach((val, j) => {
        if (typeof val === "number" && (val < 0 || val > 5000)) {
          errors.push(`Node ${i + 1}: conductance[${j}] = ${val} is out of range [0, 5000]`);
        }
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadJson(data: unknown, filename: string): void {
  triggerDownload(JSON.stringify(data, null, 2), filename, "application/json");
}

export function downloadCsvFromRows(rows: string[][], filename: string): void {
  const content = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  triggerDownload(content, filename, "text/csv;charset=utf-8;");
}

export function exportNodeSpecsAsJson(nodes: NodeSpecItem[]): void {
  downloadJson(nodes, "thermal-node-specs.json");
}

export function exportConductanceMatrixAsJson(nodes: NodeSpecItem[]): void {
  const active = nodes.filter((n) => n.status !== "unavailable");
  const data: Record<string, unknown> = {};
  active.forEach((node) => {
    data[`node_${node.id}`] = { conductance: node.values?.node_conductance ?? [] };
  });
  downloadJson(data, "thermal-conductance-matrix.json");
}

export function exportConductanceMatrixAsCsv(nodes: NodeSpecItem[]): void {
  const rows = buildConductanceCsvRows(nodes.filter((n) => n.status !== "unavailable"));
  downloadCsvFromRows(rows, "thermal-conductance-matrix.csv");
}

export function exportThermalConductivityAsJson(nodes: NodeSpecItem[]): void {
  const configured = nodes.filter((n) => n.status === "complete");
  const data: Record<string, unknown> = {};
  configured.forEach((node) => {
    data[`node_${node.id}`] = { conductance: node.values?.node_conductance ?? [] };
  });
  downloadJson(data, "thermal-conductivity.json");
}

export function exportThermalConductivityAsCsv(nodes: NodeSpecItem[]): void {
  const rows = buildConductanceCsvRows(nodes.filter((n) => n.status === "complete"));
  downloadCsvFromRows(rows, "thermal-conductivity.csv");
}

function buildConductanceCsvRows(nodes: NodeSpecItem[]): string[][] {
  const header = ["", ...nodes.map((n) => n.name)];
  const rows = nodes.map((rowNode) => {
    const conductance = rowNode.values?.node_conductance ?? [];
    return [rowNode.name, ...nodes.map((_, colIdx) => String(conductance[colIdx] ?? 0))];
  });
  return [header, ...rows];
}

function collectSubsystemDeckLabels(thermalModel: ThermalModelConfig | undefined): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  thermalModel?.panels?.forEach((panel) => {
    (panel.cells ?? []).forEach((cell) => {
      (cell.subsystems ?? []).forEach((sub) => {
        if (!map.has(sub.displayName)) map.set(sub.displayName, new Set());
        map.get(sub.displayName)!.add(panel.panelLabel);
      });
    });
  });
  return map;
}

const rangeCell = (value: number | null | undefined): string => (value === null || value === undefined ? "" : String(value));

export function exportSurfaceThresholdsAsJson(thermalModel: ThermalModelConfig | undefined): void {
  downloadJson(thermalModel?.subsystemThresholds ?? {}, "surface-thresholds.json");
}

export function exportSurfaceThresholdsAsCsv(thermalModel: ThermalModelConfig | undefined): void {
  const deckLabelsByName = collectSubsystemDeckLabels(thermalModel);
  const thresholdsMap = thermalModel?.subsystemThresholds ?? {};

  const headerRow1 = ["Deck", "Component", ...THRESHOLD_LEVELS.flatMap((level) => [level.label, ""])];
  const headerRow2 = ["", "", ...THRESHOLD_LEVELS.flatMap(() => ["Min", "Max"])];

  const dataRows = Array.from(deckLabelsByName.entries()).map(([displayName, deckLabels]) => {
    const values = thresholdsMap[displayName] ?? createEmptyThresholds();
    return [
      Array.from(deckLabels).join("/"),
      displayName,
      ...THRESHOLD_LEVELS.flatMap((level) => [rangeCell(values[level.key].min), rangeCell(values[level.key].max)]),
    ];
  });

  downloadCsvFromRows([headerRow1, headerRow2, ...dataRows], "surface-thresholds.csv");
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

const parseNumberCell = (cell: string | undefined): number | null => {
  const trimmed = (cell ?? "").trim();
  if (trimmed === "" || trimmed.toUpperCase() === "NA") return null;
  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

export function parseSurfaceThresholdsCsv(csvText: string): SubsystemThresholdsMap {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 3) return {};

  const stripQuotes = (s: string) => s.trim().replace(/^"|"$/g, "");
  const result: SubsystemThresholdsMap = {};

  for (let i = 2; i < lines.length; i++) {
    const cells = lines[i].split(",").map(stripQuotes);
    const displayName = cells[1];
    if (!displayName) continue;

    const thresholds: Thresholds = createEmptyThresholds();
    THRESHOLD_LEVELS.forEach((level, levelIdx) => {
      const minCellIdx = 2 + levelIdx * 2;
      const maxCellIdx = minCellIdx + 1;
      thresholds[level.key] = {
        min: parseNumberCell(cells[minCellIdx]),
        max: parseNumberCell(cells[maxCellIdx]),
      };
    });

    result[displayName] = thresholds;
  }

  return result;
}

export function applySurfaceThresholdsToThermalModel(
  parsed: SubsystemThresholdsMap,
  thermalModel: ThermalModelConfig
): ThermalModelConfig {
  return {
    ...thermalModel,
    subsystemThresholds: {
      ...thermalModel.subsystemThresholds,
      ...parsed,
    },
  };
}

export function parseConductanceCsv(csvText: string): Record<string, Record<string, number>> {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return {};

  const stripQuotes = (s: string) => s.trim().replace(/^"|"$/g, "");
  const headerCells = lines[0].split(",").map(stripQuotes);
  const colNames = headerCells.slice(1);

  const result: Record<string, Record<string, number>> = {};
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(",").map(stripQuotes);
    const rowName = cells[0];
    if (!rowName) continue;
    result[rowName] = {};
    colNames.forEach((colName, j) => {
      result[rowName][colName] = parseFloat(cells[j + 1]) || 0;
    });
  }
  return result;
}

export function applyConductanceCsvToNodes(
  csvMatrix: Record<string, Record<string, number>>,
  nodes: NodeSpecItem[]
): NodeSpecItem[] {
  return nodes.map((node) => {
    const rowData = csvMatrix[node.name];
    if (!rowData || !node.values) return node;
    const newConductance = nodes.map((colNode) => rowData[colNode.name] ?? 0);
    return { ...node, values: { ...node.values, node_conductance: newConductance } };
  });
}

export function applyConductanceJsonToNodes(
  conductanceJson: Record<string, { conductance: number[] }>,
  nodes: NodeSpecItem[]
): NodeSpecItem[] {
  return nodes.map((node) => {
    const entry = conductanceJson[`node_${node.id}`];
    if (!entry || !Array.isArray(entry.conductance) || !node.values) return node;
    return { ...node, values: { ...node.values, node_conductance: entry.conductance } };
  });
}
